import React from "react";
import { Lead, Section, P, Bullets, Callout, CTA } from "../../../../blog/components";

export const title = "El problema de interfaz de los agentes de programación con IA";
export const excerpt =
  "El terminal se diseñó para que una persona escribiera un comando cada vez. Los agentes de IA lo convierten en un lugar donde muchos procesos semiautónomos actúan a la vez, y nuestros patrones de interfaz no han seguido el ritmo.";
export const heroAlt = "Paisaje cinematográfico estilo Moebius a la hora dorada";
export const readingTime = "6 min de lectura";

/** In-text footnote marker: a real link to the cited source (opens in a new tab). */
function Fn({ n }: { n: number }) {
  const ref = REFERENCES[n - 1];
  return (
    <sup style={{ fontSize: "0.72em", lineHeight: 0, marginLeft: 1 }}>
      <a
        href={ref ? ref.href : `#fn-${n}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#5ba8f5", textDecoration: "none", fontWeight: 600 }}
      >
        [{n}]
      </a>
    </sup>
  );
}

const REFERENCES: { text: string; href: string; label: string }[] = [
  {
    text: "OpenAI, «Codex CLI», documentación para desarrolladores de OpenAI. Codex CLI se describe como un agente de programación local en el terminal capaz de leer, modificar y ejecutar código en el directorio seleccionado.",
    href: "https://developers.openai.com/codex/cli",
    label: "developers.openai.com/codex/cli",
  },
  {
    text: "Anthropic, «Agent SDK overview», documentación de Claude Code. El Agent SDK crea agentes que pueden leer archivos, ejecutar comandos, buscar en la web, editar código y reutilizar las herramientas, el bucle del agente y la gestión de contexto que mueven Claude Code.",
    href: "https://code.claude.com/docs/en/agent-sdk/overview",
    label: "code.claude.com/docs/en/agent-sdk/overview",
  },
  {
    text: "GitHub, «About Copilot coding agent», documentación de GitHub. El agente de programación de Copilot puede investigar un repositorio, planificar una implementación, modificar código en una rama, dejar que el desarrollador revise el diff y abrir una pull request.",
    href: "https://docs.github.com/copilot/concepts/agents/coding-agent/about-coding-agent",
    label: "docs.github.com/copilot/…/about-coding-agent",
  },
  {
    text: "Git, «git-worktree Documentation», documentación oficial de Git. git worktree permite que un mismo repositorio admita varios árboles de trabajo, de modo que puede haber más de una rama activa a la vez.",
    href: "https://git-scm.com/docs/git-worktree",
    label: "git-scm.com/docs/git-worktree",
  },
  {
    text: "Anthropic, «Tool use overview», documentación de la API de Claude. El uso de herramientas se plantea como un bucle agéntico en el que Claude decide cuándo llamar a una herramienta y, según su tipo, la ejecuta la aplicación o Anthropic.",
    href: "https://platform.claude.com/docs/en/agents-and-tools/tool-use/overview",
    label: "platform.claude.com/docs/…/tool-use/overview",
  },
];

export default function Post() {
  return (
    <>
      <Lead>Durante mucho tiempo, el terminal fue la parte más simple del flujo de trabajo del desarrollador.</Lead>

      <P>
        Abrías una shell, lanzabas comandos, mirabas la salida, cambiabas de directorio, quizá
        dividías un panel, y seguías. Era una superficie fina y rápida para ejecutar cosas directamente, no un
        espacio de trabajo completo.
      </P>
      <P>
        Los agentes de programación con IA han cambiado eso. Escriben código. Y, lo que es más importante, ahora operan
        dentro del mismo entorno donde el desarrollador hace el trabajo con más estado: ejecutar
        pruebas, leer logs, cambiar de rama, levantar servidores, inspeccionar archivos y depurar
        fallos.
      </P>
      <P>
        Eso convierte al terminal en su hogar natural. Codex CLI, de OpenAI, se ejecuta en local desde el
        terminal y puede leer, modificar y ejecutar código en un directorio.<Fn n={1} /> El Claude
        Agent SDK de Anthropic crea agentes que leen archivos, ejecutan comandos y editan código con el mismo bucle
        de agente y la misma gestión de contexto que hay detrás de Claude Code.<Fn n={2} /> El agente de programación de Copilot, de GitHub,
        investiga un repositorio, planifica, modifica código en una rama y abre una pull request.<Fn n={3} />
      </P>
      <P>
        Así que el terminal ya no es solo donde escribes comandos. Se está convirtiendo en un lugar donde
        varios procesos semiautónomos actúan, se detienen, piden aprobación, modifican archivos y esperan a que
        vuelvas a entrar en el bucle. La mayoría de nuestros patrones de interfaz se diseñaron para ejecutar comandos a mano,
        no para supervisar agentes de larga duración.
      </P>

      <Section title="El modelo de siempre: un comando, un resultado">
        <P>
          La UX clásica del terminal es lineal. Escribes un comando, la máquina responde, lees la
          salida y decides el siguiente. Las pestañas, los paneles y los multiplexores nos dieron más sitios donde
          ejecutar comandos, no una forma nueva de entender el trabajo en curso.
        </P>
        <P>
          Una sesión de agente rompe eso. No es solo salida; es una tarea con intención, contexto,
          estado, permisos y efectos secundarios. Se parece más a un objeto de trabajo vivo que a un búfer de
          terminal. Pero la seguimos tratando como texto en una pestaña.
        </P>
      </Section>

      <Section title="El modelo nuevo: muchas tareas, muchos contextos">
        <P>
          En cuanto un agente te resulta útil, quieres varios: uno arreglando una prueba, otro explorando una refactorización, otro
          revisando una pull request, otro corriendo un experimento en otra rama.
        </P>
        <P>
          Por eso importan los worktrees de Git. Permiten que un mismo repositorio mantenga varios árboles de trabajo, con
          más de una rama activa a la vez.<Fn n={4} /> Los agentes actúan dentro de un sistema de archivos, así que si
          comparten checkout, servidor, puerto o base de datos, los resultados se vuelven ambiguos y a la persona le toca
          reconstruir qué pasó. El flujo de trabajo, en la práctica, se vuelve espacial aunque la interfaz no lo
          sea. Empiezas a pensar por zonas:
        </P>
        <Bullets
          items={[
            "Una rama, un servidor, una prueba que falla.",
            "Un agente con una corrección conservadora, otro con una agresiva.",
            "Una vista previa, un flujo de logs, un diff.",
          ]}
        />
        <P>Eso ya no es un problema del terminal. Es un problema del espacio de trabajo.</P>
      </Section>

      <Section title="Las pestañas no conservan la intención">
        <P>
          Una pestaña te dice que existe un terminal. Pocas veces te dice por qué: a qué tarea pertenece, en qué
          rama está, si tocó archivos, si es seguro cerrarlo o si el agente
          de dentro te está esperando. Para una shell, vale. No vale cuando el terminal alberga
          un agente que lleva diez minutos razonando, ha editado seis archivos y ahora pide aplicar una
          migración delicada.
        </P>
        <P>Un buen espacio de trabajo para agentes debería responder, de un vistazo:</P>
        <Bullets
          items={[
            "¿Qué intenta hacer este agente y sobre qué estado?",
            "¿Qué ha cambiado y qué ha probado ya?",
            "¿Está bloqueado, esperando, ha fallado o sigue trabajando?",
            "¿Puedo comparar dos intentos y retomarlo mañana?",
          ]}
        />
        <P>
          Y la salida de un agente no es un log. Una parte son decisiones, suposiciones, planes y límites
          de permisos. Un log de compilación se puede ojear; la transcripción de un agente hay que interpretarla.
          Los agentes nativos del terminal, como Codex CLI y Claude Code, están pegados al entorno real,
          <Fn n={1} /><Fn n={2} /> lo que los hace potentes pero obliga a que la interfaz comunique algo más
          que texto.
        </P>
      </Section>

      <Section title="Supervisar es la parte difícil">
        <Callout>
          Lanzar un agente es fácil. Supervisar uno cuesta más. Supervisar varios cuesta muchísimo más.
        </Callout>
        <P>
          Un cuadro de chat junto a un árbol de archivos sirve para tareas pequeñas. Se queda corto en cuanto el trabajo se vuelve
          operativo y necesita terminales, diffs, vistas previas, resultados de pruebas, logs y ramas. Cada
          enfoque tiene su pega:
        </P>
        <Bullets
          items={[
            "Un agente en el IDE está cerca del código, pero no siempre del tiempo de ejecución.",
            "Un agente en el terminal está cerca del tiempo de ejecución, pero no siempre ordenado.",
            "Un agente de pull request en la nube como Copilot encaja en la revisión, no en el pilotaje en directo.",
          ]}
        />
        <P>
          Copilot se construye en torno al trabajo por ramas y a un diff revisable;<Fn n={3} /> Codex CLI vive
          en el terminal;<Fn n={1} /> el Claude Agent SDK expone de forma programática el mismo bucle de archivos, comandos y
          edición de código.<Fn n={2} /> Ninguno es la respuesta completa. Así que la
          pregunta es sencilla: ¿qué interfaz mantiene al desarrollador al mando cuando el trabajo útil pasa en
          más de un sitio?
        </P>
      </Section>

      <Section title="Muestra el estado, no solo los flujos">
        <P>
          Un espacio de trabajo mejor trata cada tarea como un objeto de primera clase, no solo como una pestaña, una transcripción o una
          rama, y lleva consigo su instrucción, su directorio de trabajo, su rama o worktree, los comandos ejecutados, los archivos
          modificados, su estado, las aprobaciones y tus notas.
        </P>
        <P>
          Esto no oculta el terminal. El terminal es honesto: muestra la superficie real de comandos
          y se deja inspeccionar. La interfaz no debería sustituirlo, sino envolverlo con memoria,
          estructura y navegación, igual que ya organizamos las ventanas físicamente como memoria
          externa.
        </P>
        <P>
          La seguridad entra aquí. «¿Permitir el comando?» se queda flojo si no sabes qué tarea lo pidió, qué
          ha tocado y qué va a cambiar. Cuanto más agéntica es la herramienta, más tiene que mostrar la interfaz la
          procedencia: quién lo inició, desde qué instrucción, sobre qué estado y qué se puede
          revertir. Anthropic plantea el bucle agéntico como el modelo decidiendo llamar a una herramienta y la
          aplicación o Anthropic ejecutándola;<Fn n={5} /> una buena interfaz hace visibles esos límites.
        </P>
      </Section>

      <Section title="El terminal ganó porque es componible">
        <P>
          La tentación es montar un único centro de mando gigante con forma de IDE. Seguramente sea el
          instinto equivocado. El terminal perduró porque compone: funciona con cualquier lenguaje, framework,
          gestor de paquetes o script local medio roto, porque sabe muy poco y deja que otras
          herramientas hablen a través de él. Así que un espacio de trabajo para agentes debería ser menos un «IDE de IA» y más una
          superficie de coordinación:
        </P>
        <Bullets
          items={[
            "Los terminales, las ramas, los diffs y los logs siguen siendo reales.",
            "Los agentes se vuelven visibles como trabajadores con estado.",
            "La persona sigue siendo quien decide qué importa.",
          ]}
        />
      </Section>

      <Section title="El verdadero cuello de botella es la atención">
        <P>
          Durante años, las herramientas de desarrollo se optimizaron para escribir rápido y autocompletar. Con los agentes, el
          cuello de botella pasa de las pulsaciones a la atención: ¿entiendo qué está pasando, me fío de este cambio, comparo
          dos intentos, lo retomo mañana, cierro el proceso correcto y sé distinguir si un agente está atascado o
          de verdad trabajando?
        </P>
        <P>
          La siguiente capa útil seguramente no sea un chat más listo, sino un modelo de espacio de trabajo que
          entienda tareas antes que mensajes, worktrees antes que carpetas, estado del agente antes que salida, aprobaciones
          antes que prompts e historial antes que scrollback. El terminal seguirá ahí, más que nunca.
          Pero puede que no baste cuando la unidad de trabajo sea más grande que un comando.
        </P>
        <P>Las viejas superficies empiezan a tapar lo más importante:</P>
        <p style={{ margin: "8px 0 0", fontSize: 22, fontWeight: 600, letterSpacing: "-0.01em", color: "var(--text-primary)" }}>
          ¿Qué está pasando realmente?
        </p>
      </Section>

      <Callout>
        CATE es nuestro intento de esa superficie de coordinación: terminales, editores, navegadores y agentes como
        ventanas de primera clase sobre un único canvas infinito.
      </Callout>
      <CTA label="Conoce CATE" href="/" />

      <Section title="Referencias">
        <ol style={{ margin: 0, paddingLeft: 22, display: "flex", flexDirection: "column", gap: 12 }}>
          {REFERENCES.map((r, i) => (
            <li key={i} id={`fn-${i + 1}`} style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--text-muted)" }}>
              {r.text}{" "}
              <a href={r.href} target="_blank" rel="noopener noreferrer" style={{ color: "#5ba8f5", wordBreak: "break-word" }}>
                {r.label}
              </a>
            </li>
          ))}
        </ol>
      </Section>
    </>
  );
}
